import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

const STAGES = [
  { t: '0:00', label: 'Book',                desc: '2 minutes',           ico: '📋',  lab: false },
  { t: '0:02', label: 'Trustian arrives',    desc: 'Within 60 min',       ico: '🏍',  lab: false },
  { t: '1:00', label: 'Sample drawn',        desc: 'Barcoded, sealed',    ico: '🩸',  lab: false },
  { t: '1:05', label: 'Cold-chain transit',  desc: '2–8 °C, GPS-tracked', ico: '🧊',  lab: true  },
  { t: '2:30', label: 'Lab Processing',    desc: 'TDPL local labs',    ico: '🧪',  lab: true  },
  { t: '5:30', label: 'Pathologist review',  desc: 'Signed',           ico: '👨‍⚕️', lab: true  },
  { t: '6:00', label: 'Report delivered',    desc: 'WhatsApp, email',     ico: '📱',  lab: false },
];

const TEAL = '#00B49A';
const AMBER = '#F0B429';
const INK = '#FAFAF5';
const MIST = '#B8C5BC';

const HowItWorks = () => {
  const sectionRef = useRef(null);
  const startedRef = useRef(false);
  const [trigger, setTrigger] = useState(0);
  const [progress, setProgress] = useState(-1);

  useEffect(() => {
    if (!sectionRef.current) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            setTrigger((n) => n + 1);
          }
        });
      },
      { threshold: 0.2 },
    );
    obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (trigger === 0) return undefined;
    setProgress(-1);
    const ids = [];
    for (let i = 0; i < STAGES.length; i += 1) {
      ids.push(setTimeout(() => setProgress(i), 350 + i * 380));
    }
    return () => ids.forEach(clearTimeout);
  }, [trigger]);

  return (
    <Box
      ref={sectionRef}
      sx={{
        bgcolor: 'var(--forest)',
        color: '#fff',
        py: { xs: 7, md: 10 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '0.2em',
              color: TEAL,
              mb: 1.5,
              lineHeight: 1,
            }}
          >
            — YOUR SAMPLE&apos;S JOURNEY —
          </Typography>
          <Typography
            className="serif"
            sx={{
              fontSize: { xs: 22, sm: 26, md: 30, lg: 34 },
              fontWeight: 500,
              color: INK,
              lineHeight: 1.25,
              mb: 1.5,
            }}
          >
            From your doorstep to your phone, in six hours.
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 12, md: 13 },
              color: MIST,
              maxWidth: 480,
              mx: 'auto',
              lineHeight: 1.5,
            }}
          >
            Every step monitored. Every handover logged. Every sample traceable.
          </Typography>
        </Box>

        {/* Stage row */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-start',
            gap: { xs: 0.5, md: 0.5 },
            px: { xs: 0, md: 0.5 },
          }}
        >
          {/* Dashed connector line — sits at vertical centre of the circles */}
          <Box
            sx={{
              position: 'absolute',
              top: { xs: 46, sm: 56, md: 60 },
              left: '8%',
              right: '8%',
              height: '1px',
              backgroundImage:
                'repeating-linear-gradient(to right, rgba(0,180,154,0.55) 0, rgba(0,180,154,0.55) 4px, transparent 4px, transparent 8px)',
              transform: progress >= 0 ? 'scaleX(1)' : 'scaleX(0)',
              transformOrigin: 'left center',
              transition: 'transform 3s ease-out',
              zIndex: 0,
              pointerEvents: 'none',
            }}
          />

          {STAGES.map((s, i) => {
            const visible = progress >= i;
            return (
              <Box
                key={s.label}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  textAlign: 'center',
                  position: 'relative',
                  zIndex: 1,
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'opacity 0.55s ease-out, transform 0.55s ease-out',
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 500,
                    color: TEAL,
                    letterSpacing: '0.05em',
                    mb: 0.75,
                    lineHeight: 1,
                  }}
                >
                  {s.t}
                </Typography>

                <Box
                  sx={{
                    position: 'relative',
                    width: { xs: 52, sm: 64, md: 78 },
                    height: { xs: 52, sm: 64, md: 78 },
                    borderRadius: '50%',
                    bgcolor: s.lab ? 'rgba(0,180,154,0.15)' : 'rgba(0,180,154,0.08)',
                    border: '1.5px solid',
                    borderColor: s.lab ? AMBER : TEAL,
                    mx: 'auto',
                    mb: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: { xs: 20, sm: 24, md: 28 },
                    lineHeight: 1,
                  }}
                >
                  {s.ico}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -3,
                      right: 4,
                      width: 20,
                      height: 20,
                      bgcolor: AMBER,
                      color: '#1A3A2A',
                      borderRadius: '50%',
                      fontSize: 10,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      animation: 'sjPulse 2.2s ease-in-out infinite',
                      '@keyframes sjPulse': {
                        '0%, 100%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.12)' },
                      },
                    }}
                  >
                    {i + 1}
                  </Box>
                </Box>

                <Typography
                  sx={{
                    fontSize: { xs: 11, md: 12 },
                    fontWeight: 500,
                    color: INK,
                    lineHeight: 1.3,
                    mb: 0.5,
                    px: 0.25,
                  }}
                >
                  {s.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 9.5, md: 10 },
                    color: MIST,
                    lineHeight: 1.45,
                    px: 0.25,
                  }}
                >
                  {s.desc}
                </Typography>
              </Box>
            );
          })}
        </Box>

      </Container>
    </Box>
  );
};

export default HowItWorks;
